import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { RadioButton } from '@progress/kendo-react-inputs';
import { cloneDeep, isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BulkEditLineItemColumns,
  initialBulkEditLineItemData,
} from 'src/assets/static/invoice_details/BulkEditLineItemColumns';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import { useContextSelector } from 'use-context-selector';
import { BulkLineItem } from './BulkAddLineItems';
import useBulkLineItemErrors, {
  BulkLineItemError,
  BulkLineItemInfo,
  BulkLineItemWarning,
} from './errors/useBulkLineItemErrors';
import { InvoiceContext } from './InvoiceDetailsContext';

const BulkEditLineItems = () => {
  const { confirm } = useConfirm();
  const [openModal, setOpenModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'all' | 'blank'>('all');
  const { t } = useTranslation();
  const [data, setData] = useState(cloneDeep(initialBulkEditLineItemData));

  const throttledInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceLineItems
  );
  const selectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.selectedLineItemIds
  );
  const setInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceLineItems
  );

  const { selectedLineItems, isSelected } = useMemo(() => {
    const selectedLineItems = throttledInvoiceLineItems?.filter((lineItem) =>
      selectedLineItemIds.includes(lineItem.receiptLineItemId)
    );
    return {
      selectedLineItems,
      isSelected: !!selectedLineItems?.length,
    };
  }, [throttledInvoiceLineItems, selectedLineItemIds]);

  const { errors, warnings, infos, errorList } = useBulkLineItemErrors(
    data,
    selectedLineItems,
    'edit'
  );

  const validatedData: BulkLineItem[] = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        errors: errors[index][item.field as keyof BulkLineItemError] || [],
        warnings: warnings[item.field as keyof BulkLineItemWarning] || [],
        infos: infos[index][item.field as keyof BulkLineItemInfo] || [],
      })),
    [data, errors, warnings, infos]
  );

  const hasDataChanged = useMemo(() => !isEqual(data, initialBulkEditLineItemData), [data]);

  const isBulkEditDirty = hasDataChanged || selectedOption !== 'all';

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      if (isBulkEditDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. To continue editing, please go back (Cancel). If you proceed, your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, isBulkEditDirty]
  );

  const handleHeaderSelectionChange = (e: GridHeaderSelectionChangeEvent) => {
    const { checked }: any = e.syntheticEvent.target;
    setData((prev) =>
      prev.map((item) => ({
        ...item,
        selected: checked,
      }))
    );
  };

  const handleSelectionChange = useCallback((e: GridSelectionChangeEvent) => {
    setData((prev) => {
      const prevItems = [...prev];
      const index = e?.startRowIndex;
      prevItems[index].selected = !prevItems[index].selected;
      return prevItems;
    });
  }, []);

  const openBulkEditModal = () => {
    setData(cloneDeep(initialBulkEditLineItemData));
    setSelectedOption('all');
    setOpenModal(true);
  };
  const closeBulkEditModal = () => {
    confirmBeforeProceeding(() => {
      setOpenModal(false);
    });
  };

  const handleBulkEditLineItems = () => {
    const itemNumber = data.find((item) => item.field === 'productItemNum');
    const description = data.find((item) => item.field === 'description');
    const glCd = data.find((item) => item.field === 'glCd');
    const priceAmt = data.find((item) => item.field === 'priceAmt');
    const uom = data.find((item) => item.field === 'uom');
    const quantityReceived = data.find((item) => item.field === 'quantityReceived');
    const taxable = data.find((item) => item.field === 'isTaxable');

    setInvoiceLineItems((prev) =>
      prev.map((lineItem, index) => {
        // Only update selected line items
        if (!selectedLineItemIds.includes(lineItem.receiptLineItemId)) {
          return lineItem;
        }

        const updatedLineItem = { ...lineItem };
        const lineItemSequenceNumber = index + 1;
        const canEdit = lineItem.canDelete === 0; // PO line items have canDelete !== 0

        // Apply changes based on selectedOption
        if (selectedOption === 'all') {
          // Apply to all fields regardless of current value
          // For item and description, only apply to non-PO line items
          if (itemNumber?.selected && canEdit) {
            updatedLineItem.productItemNum =
              itemNumber.value + ` ${itemNumber.appendLineItem ? lineItemSequenceNumber : ''}`;
          }
          if (description?.selected && canEdit) {
            updatedLineItem.description =
              description.value + ` ${description.appendLineItem ? lineItemSequenceNumber : ''}`;
          }
          // Other fields can be updated for all line items
          if (glCd?.selected) {
            updatedLineItem.glCd = glCd.value;
          }
          if (priceAmt?.selected) {
            updatedLineItem.priceAmt = priceAmt.value;
          }
          if (uom?.selected) {
            updatedLineItem.uom = uom.value;
          }
          if (quantityReceived?.selected) {
            updatedLineItem.quantityReceived = quantityReceived.value;
          }
          if (taxable?.selected) {
            updatedLineItem.isTaxable = taxable.value;
          }
        } else {
          // Apply only to blank fields
          // For item and description, only apply to non-PO line items
          if (itemNumber?.selected && !lineItem.productItemNum && canEdit) {
            updatedLineItem.productItemNum =
              itemNumber.value + ` ${itemNumber.appendLineItem ? lineItemSequenceNumber : ''}`;
          }
          if (description?.selected && !lineItem.description && canEdit) {
            updatedLineItem.description =
              description.value + ` ${description.appendLineItem ? lineItemSequenceNumber : ''}`;
          }
          // Other fields can be updated for all line items
          if (glCd?.selected && !lineItem.glCd) {
            updatedLineItem.glCd = glCd.value;
          }
          if (
            priceAmt?.selected &&
            (lineItem.priceAmt === null ||
              lineItem.priceAmt === undefined ||
              lineItem.priceAmt === 0 ||
              (!lineItem.priceAmt && lineItem.priceAmt !== 0))
          ) {
            updatedLineItem.priceAmt = priceAmt.value;
          }
          if (uom?.selected && !lineItem.uom) {
            updatedLineItem.uom = uom.value;
          }
          if (
            quantityReceived?.selected &&
            (lineItem.quantityReceived === null ||
              lineItem.quantityReceived === undefined ||
              lineItem.quantityReceived === 0 ||
              (!lineItem.quantityReceived && lineItem.quantityReceived !== 0))
          ) {
            updatedLineItem.quantityReceived = quantityReceived.value;
          }
          if (taxable?.selected && lineItem.isTaxable === undefined) {
            updatedLineItem.isTaxable = taxable.value;
          }
        }

        if (quantityReceived?.selected || priceAmt?.selected) {
          updatedLineItem.subTotal =
            Number(updatedLineItem.quantityReceived || 0) * Number(updatedLineItem.priceAmt || 0);
        }

        return updatedLineItem;
      })
    );

    setOpenModal(false);
  };

  const handleItemChange = (e: GridItemChangeEvent) => {
    const { dataItem, value, field } = e;
    setData((prev) => {
      const prevItems = [...prev];
      const index = dataItem?.index as number;
      prevItems[index][field as keyof BulkLineItem] = value as never;
      return prevItems;
    });
  };

  const { messages, disableBulkEditButton } = useMemo(() => {
    const disableBulkEditButton = !isSelected;
    const messages: string[] = [];
    if (disableBulkEditButton) {
      messages.push('Please select at least 1 Line Item to Bulk Edit.');
    }
    return { messages, disableBulkEditButton };
  }, [isSelected]);

  return (
    <div>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Information" contents={messages} />
          </div>
        }
        show={!!messages.length}
      >
        <Button onClick={openBulkEditModal} disabled={disableBulkEditButton}>
          Bulk Edit {isSelected && `(${selectedLineItems.length})`}
        </Button>
      </PopUp>

      <Dialog
        width="800px"
        id="modal"
        title={`Bulk Edit Line Items ${isSelected && `(${selectedLineItems.length})`}`}
        open={openModal}
        onClose={closeBulkEditModal}
      >
        <div className="flex items-center gap-2 mb-4 mt-2 text-sm">
          <span>Apply the changes to</span>
          <div className="flex items-center gap-2">
            <RadioButton
              checked={selectedOption === 'all'}
              onChange={() => setSelectedOption('all')}
            >
              All Fields
            </RadioButton>
            <RadioButton
              checked={selectedOption === 'blank'}
              onChange={() => setSelectedOption('blank')}
            >
              Only Blank Fields or Zero Fields
            </RadioButton>
          </div>
        </div>
        <div className="mb-4 text-sm">
          <span>
            {selectedOption === 'all'
              ? 'The selected fields and values below will be applied to all selected line item(s), replacing existing values even if they are not blank.'
              : 'The selected fields and values below will be applied only to blank fields or zero fields for the selected line item(s). Existing values will not be changed.'}
          </span>
        </div>
        <GridWrapper
          id="BulkEditTable"
          DataItemKey="rowId"
          className={'auto-scrollbar'}
          columns={BulkEditLineItemColumns}
          dataSource={validatedData}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onItemChange={handleItemChange}
          hideScrollBar
          resizable={true}
        />
        <DialogActionsBar>
          <div className="flex justify-end">
            <PopUp
              content={
                <div className="m-1 max-w-[300px]">
                  <PopUpContentWrapper type="Error" contents={errorList} />
                </div>
              }
              show={errorList.length > 0}
            >
              <Button
                disabled={errorList.length > 0 || !data.some((item) => item.selected)}
                onClick={handleBulkEditLineItems}
                color="info"
              >
                {t('Apply')}
              </Button>
            </PopUp>
            <Button onClick={closeBulkEditModal} color="info">
              {t('Cancel')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

export default BulkEditLineItems;
